import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from "react-router-dom";
import moment from 'moment';

import Nav from '../../../common/components/Nav';
import Footer from '../../../common/components/Footer';
import ScrollReveal from '../../../common/components/ScrollReveal';
import { errorHandler } from '../../../utils/helpers/errorHandler';
import { toast } from 'react-toastify';

import { fetchPage } from "../../../utils/api/cms/page";

import "./detail.css";

const rootUrl = `${process.env.REACT_APP_ROOT_URL}`;

/* There is no `category` column on CMS pages, so the tag label is derived
   from the page `type`, which is the only taxonomy the API exposes. */
const CATEGORY_LABELS = {
  quick_reads: 'Quick Read',
  budgets: 'Fiscal Transparency',
  analysis: 'Analysis',
  datasets: 'Dataset',
  pages: 'Article',
};

const PageDetails = () => {
  const { slug } = useParams();

  const [publicPage, setPublicPage] = useState(undefined),
    [isLoading, setIsLoading] = useState(true),
    [hasError, setHasError] = useState(false);

  const handleFetchPublicPage = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const data = await fetchPage(slug);

      setPublicPage(data);
    } catch (error) {
      setHasError(true);
      errorHandler(error, toast);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleFetchPublicPage();
  }, [slug]);

  /* ---------- Derived data ---------- */

  const bannerUrl = useMemo(() => {
    if (!publicPage?.banner) return null;

    return publicPage.banner.startsWith('/')
      ? `${rootUrl}/${publicPage.banner.substr(publicPage.banner.lastIndexOf('/') + 1)}`
      : `${rootUrl}/uploads/${publicPage.banner}`;
  }, [publicPage]);

  const publishedDate = useMemo(() => {
    if (!publicPage?.createdAt) return null;
    return moment(publicPage.createdAt).format('LLLL');
  }, [publicPage]);

  const categoryLabel =
    publicPage?.category ||
    CATEGORY_LABELS[publicPage?.type] ||
    'Article';

  const hasBody = Boolean(publicPage?.body);

  /* ---------- Loading ---------- */
  if (isLoading) {
    return (
      <div className="__pg-shell d-flex flex-column min-vh-100">
        <header>
          <Nav />
        </header>

        <div className="__pg-state flex-grow-1">
          <div className="__pg-state-inner">
            <img src="/assets/img/page-loader.gif" alt="Loading..." width="56" height="56" />
            <h2>Loading article</h2>
            <p>Fetching the latest from the LBS library…</p>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  /* ---------- Error / not found ---------- */
  if (hasError || !publicPage) {
    return (
      <div className="d-flex flex-column min-vh-100" style={{ backgroundColor: '#F8FAFC' }}>
        <header>
          <Nav />
        </header>

        <div className="__pg-state flex-grow-1">
          <div className="__pg-state-inner">
            <svg
              width="52"
              height="52"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94A3B8"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
            <h2>We couldn't load this page</h2>
            <p>
              The article may have been removed or is temporarily unavailable. Please try again
              in a moment.
            </p>
            <Link
              to="/"
              className="btn text-white rounded-pill px-4 py-2 fw-semibold border-0"
              style={{ backgroundColor: '#0C9EA9', fontSize: '14px' }}
            >
              ← Back to Homepage
            </Link>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  /* ---------- Article: read-only, Instagram-inspired two column post ---------- */
  return (
    <div className="__pg-shell d-flex flex-column min-vh-100">
      <header>
        <Nav />
      </header>

      <main className="flex-grow-1">
        <ScrollReveal>
          <section className="__pg-frame">
            {/* Cover image — fills the full height of the panel */}
            <div className="__pg-media">
              {bannerUrl ? (
                <img
                  className="__pg-media-img"
                  src={bannerUrl}
                  alt={publicPage.title || 'Article'}
                />
              ) : (
                <div className="__pg-media-fallback" />
              )}
            </div>

            {/* Reading panel */}
            <div className="__pg-panel">
              <div className="__pg-panel-scroll">
                <span className="__pg-tag">{categoryLabel}</span>

                <h1 className="__pg-headline">{publicPage.title || 'Public Pages'}</h1>

                <div className="__pg-rule" />

                {hasBody ? (
                  <div
                    className="__pg-prose"
                    dangerouslySetInnerHTML={{ __html: publicPage.body }}
                  />
                ) : (
                  <p className="__pg-empty">
                    This article doesn't have any content yet. Please check back soon.
                  </p>
                )}

                {publishedDate && <p className="__pg-date">{publishedDate}</p>}
              </div>

              <div className="__pg-footbar">
                <Link to="/" className="__pg-backlink">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  Back to Home
                </Link>

                <Link to="/publications" className="__pg-backlink __pg-backlink-alt">
                  View Publications
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </Link>
              </div>
            </div>
          </section>
        </ScrollReveal>
      </main>

      <Footer />
    </div>
  );
};

export default PageDetails;
